import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AudioLines, CheckCircle2, Clock3, Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { api, errMessage } from "../../lib/api";

// New Sound Requests: parents ask for a speech sound that isn't in the video
// library yet, see its status here, and get an email when the video arrives.
export const SoundRequests = () => {
  const [requests, setRequests] = useState(null);
  const [languages, setLanguages] = useState({ en: "English" });
  const [sound, setSound] = useState("");
  const [language, setLanguage] = useState("en");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/sound-requests")
      .then(({ data }) => {
        setRequests(data.requests);
        setLanguages(data.languages);
      })
      .catch(() => setRequests([]));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!sound.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sound-requests", { sound: sound.trim(), language });
      if (data.status === "already_available") {
        toast.success(`Good news — "${data.title || sound}" is already in the practice library with a video!`);
        setSound("");
      } else {
        toast.success(`Request sent! We'll email you when the '${sound.trim()}' sound video arrives.`);
        setSound("");
        load();
      }
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sound-requests/${id}`);
      setRequests((rs) => rs.filter((r) => r.request_id !== id));
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="sound-requests-panel">
      <div>
        <h3 className="text-sm font-bold font-display flex items-center gap-2">
          <AudioLines className="h-4 w-4 text-secondary" aria-hidden="true" /> Request a speech sound
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Missing a sound your child is working on? Ask for it here — we'll email
          you the moment its mouth-model video is live in the practice menu.
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <input
          value={sound}
          onChange={(e) => setSound(e.target.value)}
          maxLength={40}
          placeholder={'Sound, like "th" or "ch"'}
          className="flex-1 min-w-32 rounded-lg border border-border bg-background px-3 py-2 text-sm"
          aria-label="Speech sound to request"
          data-testid="sound-request-input"
        />
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="rounded-lg border border-border bg-background px-2 py-2 text-sm font-semibold"
          aria-label="Language"
          data-testid="sound-request-language"
        >
          {Object.entries(languages).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <Button
          type="submit"
          size="sm"
          disabled={busy || !sound.trim()}
          className="rounded-lg"
          data-testid="sound-request-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : "Request"}
        </Button>
      </form>

      {requests === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
      ) : requests.length > 0 && (
        <ul className="space-y-2" data-testid="sound-request-list">
          {requests.map((r) => (
            <li
              key={r.request_id}
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-2"
              data-testid={`sound-request-${r.request_id}`}
            >
              {r.status === "ready" ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" aria-hidden="true" />
              ) : (
                <Clock3 className="h-4 w-4 text-amber-500 shrink-0" aria-hidden="true" />
              )}
              <span className="text-sm font-semibold flex-1 truncate">
                '{r.sound}' <span className="text-muted-foreground font-normal">({languages[r.language] || r.language})</span>
              </span>
              <span
                className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${
                  r.status === "ready"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                }`}
                data-testid={`sound-request-status-${r.request_id}`}
              >
                {r.status === "ready" ? "Ready — in the practice menu!" : "Requested"}
              </span>
              <button
                type="button"
                onClick={() => remove(r.request_id)}
                className="text-muted-foreground hover:text-destructive p-1"
                aria-label={`Remove request for ${r.sound}`}
                data-testid={`sound-request-delete-${r.request_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
